import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

type SupportConversation = {
  userUUID: string;
  lastMessage: string;
  lastAt: string;
  lastReceived: boolean;
};

type SupportChatMessage = {
  _id: string;
  body: string;
  received: boolean;
  createdAt: string;
};

function formatRelativeTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function truncatePreview(value: string, maxLength = 120) {
  const trimmed = value.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 1)}…`;
}

export function SupportChats() {
  const navigate = useNavigate();
  const { userUUID: selectedUserUUID } = useParams<{ userUUID?: string }>();
  const isListOnly = !selectedUserUUID;
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [messages, setMessages] = useState<SupportChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    const crumbs = [{ label: "Support", path: "/support" }];
    if (selectedUserUUID) {
      crumbs.push({
        label: selectedUserUUID.slice(0, 8),
        path: `/support/${selectedUserUUID}`,
      });
    }
    setBreadcrumbs(crumbs);
    return () => {
      clearBreadcrumbs();
    };
  }, [selectedUserUUID, setBreadcrumbs, clearBreadcrumbs]);

  const refreshConversations = useCallback(async ({ initial = false } = {}) => {
    if (initial) {
      setIsLoading(true);
    }

    try {
      const response = await fetch("/api/support-chat/conversations");
      if (!response.ok) return;
      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error("Failed to fetch support conversations:", error);
    } finally {
      if (initial) {
        setIsLoading(false);
      }
    }
  }, []);

  const loadMessages = useCallback(
    async ({ initial = false }: { initial?: boolean } = {}) => {
      if (!selectedUserUUID) return;

      if (initial) {
        setThreadLoading(true);
      }

      try {
        const response = await fetch(
          `/api/support-chat/conversations/${encodeURIComponent(selectedUserUUID)}`,
        );
        if (!response.ok) return;
        const data = await response.json();
        setMessages(data.messages || []);
      } catch (error) {
        console.error("Failed to fetch support messages:", error);
        if (initial) {
          setMessages([]);
        }
      } finally {
        if (initial) {
          setThreadLoading(false);
        }
      }
    },
    [selectedUserUUID],
  );

  useEffect(() => {
    void refreshConversations({ initial: true });
  }, [refreshConversations]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void refreshConversations();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshConversations]);

  useEffect(() => {
    if (!selectedUserUUID) {
      setMessages([]);
      setThreadLoading(false);
      return;
    }

    void loadMessages({ initial: true });
  }, [loadMessages, selectedUserUUID]);

  useEffect(() => {
    if (!selectedUserUUID) return;

    const intervalId = window.setInterval(() => {
      void loadMessages();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [loadMessages, selectedUserUUID]);

  useEffect(() => {
    if (!selectedUserUUID) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, selectedUserUUID]);

  async function handleSend() {
    if (!selectedUserUUID || !replyText.trim() || isSending) return;

    setIsSending(true);
    try {
      const response = await fetch(
        `/api/support-chat/conversations/${encodeURIComponent(selectedUserUUID)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: replyText.trim() }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(data.error || "Failed to send message");
        return;
      }

      if (data.message) {
        setMessages((current) => [...current, data.message]);
      }
      setReplyText("");
      await refreshConversations();
    } catch (error) {
      console.error("Failed to send support reply:", error);
      alert("Failed to send message");
    } finally {
      setIsSending(false);
    }
  }

  return (
    <div className="flex h-[calc(100dvh-4rem)] min-h-[32rem] overflow-hidden rounded-xl border bg-background">
      <div
        className={`flex w-full flex-col border-r md:w-96 md:max-w-[40%] ${
          isListOnly ? "flex" : "hidden md:flex"
        }`}
      >
        <div className="border-b px-4 py-3">
          <h1 className="text-lg font-semibold">Support</h1>
          <p className="text-sm text-muted-foreground">
            Meeting support conversations
          </p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          ) : conversations.length === 0 ? (
            <p className="px-4 py-8 text-sm text-muted-foreground">
              No support conversations yet.
            </p>
          ) : (
            <div className="divide-y">
              {conversations.map((conversation) => {
                const isActive = conversation.userUUID === selectedUserUUID;
                return (
                  <button
                    key={conversation.userUUID}
                    type="button"
                    className={`w-full px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                      isActive ? "bg-muted" : ""
                    }`}
                    onClick={() =>
                      navigate(
                        `/support/${encodeURIComponent(conversation.userUUID)}`,
                      )
                    }
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">
                          Visitor {conversation.userUUID.slice(0, 8)}
                        </div>
                        <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                          {conversation.lastReceived ? "" : "You: "}
                          {truncatePreview(conversation.lastMessage)}
                        </div>
                      </div>
                      <div className="shrink-0 text-xs text-muted-foreground">
                        {formatRelativeTime(conversation.lastAt)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div
        className={`flex min-w-0 flex-1 flex-col ${
          isListOnly ? "hidden md:flex" : "flex"
        }`}
      >
        {selectedUserUUID ? (
          <>
            <div className="flex items-center gap-2 border-b px-4 py-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="md:hidden"
                aria-label="Back to conversations"
                onClick={() => navigate("/support")}
              >
                <ArrowLeft className="size-4" />
              </Button>
              <div>
                <h2 className="font-semibold">
                  Visitor {selectedUserUUID.slice(0, 8)}
                </h2>
                <p className="text-xs text-muted-foreground break-all">
                  {selectedUserUUID}
                </p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
              {threadLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                messages.map((message) => (
                  <div
                    key={message._id}
                    className={`flex ${
                      message.received ? "justify-end" : "justify-start"
                    }`}
                  >
                    <div
                      className={`max-w-[75%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                        message.received
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      {message.body}
                      <div
                        className={`mt-1 text-xs ${
                          message.received
                            ? "text-primary-foreground/70"
                            : "text-muted-foreground"
                        }`}
                      >
                        {formatRelativeTime(message.createdAt)}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="border-t p-4">
              <div className="flex items-end gap-2">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write a reply…"
                  rows={2}
                  className="min-h-[3rem] resize-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void handleSend();
                    }
                  }}
                />
                <Button
                  type="button"
                  size="icon"
                  disabled={!replyText.trim() || isSending}
                  onClick={() => void handleSend()}
                >
                  {isSending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Send className="size-4" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="hidden flex-1 items-center justify-center px-6 text-center text-sm text-muted-foreground md:flex">
            Select a conversation to view messages.
          </div>
        )}
      </div>
    </div>
  );
}
